import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/core/store";
import {
  addPosition,
  removeOrderItem,
  updateQuantity as updateItemQuantity,
  updateOrderComment,
  clearOrder,
} from "@/core/orders/reducer";
import {
  selectCurrentOrder,
  selectCartItems,
  selectCartTotal,
  selectCartItemsCount,
  selectCartStats,
} from "@/core/orders/selectors";
import {
  UseCartReturn,
  CartOperationResult,
  OrderItemModifier,
} from "@/core/orders/types";
import { Product } from "@/types/generated/graphql-types";

/**
 * Custom hook for cart operations with automatic sum calculation
 * Provides clean interface for cart state management and operations
 *
 * Features:
 * - Real-time cart total calculation via Redux selectors
 * - Optimistic updates for immediate UI feedback
 * - Type-safe operations with proper error handling
 * - Automatic cart validation and business rules
 */
export const useCart = (): UseCartReturn => {
  const dispatch = useAppDispatch();

  // Redux state selectors
  const currentOrder = useAppSelector(selectCurrentOrder);
  const cartItems = useAppSelector(selectCartItems);
  const totalSum = useAppSelector(selectCartTotal);
  const itemCount = useAppSelector(selectCartItemsCount);
  const cartStats = useAppSelector(selectCartStats);

  // Extract individual state pieces
  const comment = currentOrder.comment || "";
  const loading = currentOrder.loading;
  const error = currentOrder.error;

  /**
   * Add item to cart with optional modifiers
   * Supports quantity specification and modifier additions
   */
  const addItem = useCallback(
    (
      product: Product,
      quantity: number = 1,
      modifiers?: OrderItemModifier[],
      itemComment?: string
    ): CartOperationResult => {
      try {
        // Validate inputs
        if (!product || !product.id) {
          return {
            success: false,
            error: "Invalid product data",
          };
        }

        if (quantity <= 0) {
          return {
            success: false,
            error: "Quantity must be greater than 0",
          };
        }

        // Dispatch add action
        dispatch(
          addPosition({
            dish: product,
            quantity,
            modifiers: modifiers || [],
            comment: itemComment || "",
          })
        );

        return {
          success: true,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to add item to cart";
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [dispatch]
  );

  /**
   * Remove item from cart by UUID
   * Provides immediate feedback via optimistic update
   */
  const removeItem = useCallback(
    (itemId: string): CartOperationResult => {
      try {
        if (!itemId) {
          return {
            success: false,
            error: "Item ID is required",
          };
        }

        dispatch(removeOrderItem(itemId));

        return {
          success: true,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove item from cart";
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [dispatch]
  );

  /**
   * Update item quantity with validation
   * Ensures quantity is always positive
   */
  const updateQuantity = useCallback(
    (itemId: string, quantity: number): CartOperationResult => {
      try {
        if (!itemId) {
          return {
            success: false,
            error: "Item ID is required",
          };
        }

        if (quantity <= 0) {
          return {
            success: false,
            error: "Quantity must be greater than 0",
          };
        }

        dispatch(updateItemQuantity({ itemUUID: itemId, quantity }));

        return {
          success: true,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update item quantity";
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [dispatch]
  );

  /**
   * Update cart comment
   * Allows order-level comments for special instructions
   */
  const updateComment = useCallback(
    (newComment: string): void => {
      dispatch(updateOrderComment(newComment));
    },
    [dispatch]
  );

  /**
   * Clear entire cart
   * Resets cart to initial empty state
   */
  const clearCart = useCallback((): void => {
    dispatch(clearOrder());
  }, [dispatch]);

  /**
   * Get item by ID helper
   * Useful for component-level item operations
   */
  const getItemById = useCallback(
    (itemId: string) => {
      return cartItems.find((item) => item.uuid === itemId);
    },
    [cartItems]
  );

  /**
   * Check if cart is empty
   */
  const isEmpty = cartStats.isEmpty;

  /**
   * Get cart validation status
   * Can be extended with business rules
   */
  const isValid = cartItems.length > 0 && !error;

  return {
    // State
    cartItems,
    totalSum,
    itemCount,
    comment,
    loading,
    error,

    // Computed state
    isEmpty,
    isValid,
    cartStats,

    // Operations
    addItem,
    removeItem,
    updateQuantity,
    updateComment,
    clearCart,

    // Helpers
    getItemById,
  };
};

export default useCart;
